import type { PublicSiteData } from '../models/public-site-data';

/**
 * The live copy: nginx replaces the SSI directive inside this element with the file the API
 * rewrites whenever a specialist or a service changes. Anywhere without that — `vite dev`, a host
 * that has not mounted the file — the element holds the directive itself, which does not parse.
 */
export const LIVE_SITE_DATA_ELEMENT_ID = 'site-data';

/**
 * The copy the build rendered the page's markup with. Hydration has to start from exactly this,
 * whatever the live copy says by now, or React discards the markup instead of adopting it.
 */
export const BUILD_SITE_DATA_ELEMENT_ID = 'site-data-build';

/**
 * The embedded text as site data, or null when it is anything else.
 *
 * Checked for shape rather than trusted: the live element can hold an SSI directive, an error page
 * from a misconfigured include, or nothing at all, and every one of those must mean "ask the API".
 */
export function parseSiteData(text: string | null | undefined): PublicSiteData | null {
    if (text === null || text === undefined || text.trim() === '') {
        return null;
    }

    let value: unknown;

    try {
        value = JSON.parse(text);
    } catch {
        return null;
    }

    if (typeof value !== 'object' || value === null) {
        return null;
    }

    const { specialists, consultationTypes } = value as Record<string, unknown>;

    if (!Array.isArray(specialists) || !Array.isArray(consultationTypes)) {
        return null;
    }

    return { specialists, consultationTypes } as PublicSiteData;
}

/**
 * The data as the text of a `<script type="application/json">` element.
 *
 * `<` is escaped so a display name containing `</script>` cannot end the element early; JSON reads
 * `<` back as the same character.
 */
export function serializeSiteData(data: PublicSiteData): string {
    return JSON.stringify(data).replace(/</g, '\\u003c');
}
